import { AdminMiniTarget } from './admin-mini.types';
import { AdminMiniSender } from './components/AdminMiniSenderPanel';
import { AdminMiniSchedule } from './components/AdminMiniSchedulePanel';
import { ChosenNok, noksMissingAnswers } from './hooks/admin-mini-noks';

/**
 * The decisions the admin Mini screens make, kept out of the components that draw them.
 *
 * Two things live here and both are worth testing on their own: what an admin gets when they paste
 * a list of addresses, and what stops a run going out. Both are the kind of logic that is easy to
 * get subtly wrong and impossible to check by looking at a screenshot.
 */

/** Good enough to catch a typo, and not so strict that it refuses a real address. */
export const LOOKS_LIKE_AN_ADDRESS: RegExp = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * The new recipients in a pasted blob.
 *
 * The realistic way an admin gets six addresses is out of a spreadsheet, an email header or a
 * chat message, so this splits on everything those use as a separator rather than asking for one
 * per line. Anything that is not an address is dropped silently: a pasted "To:" line carries the
 * word "To" and nobody wants that reported as an error.
 *
 * **Duplicates are dropped against the existing list as well as within the paste**, which is not
 * tidiness. Two rows for one address means that person receives the same approach twice from the
 * same run, and on a mystery shop that is the one thing that gives the game away.
 *
 * @param blob what was pasted
 * @param existing who is already on the run
 */
export function parsePastedAddresses(blob: string, existing: AdminMiniTarget[]): AdminMiniTarget[] {
    const known: Set<string> = new Set(
        existing.map((target: AdminMiniTarget): string => target.email.toLowerCase())
    );

    const added: AdminMiniTarget[] = [];
    blob.split(/[\s,;<>]+/).forEach((part: string): void => {
        const address: string = part.trim();
        if (!LOOKS_LIKE_AN_ADDRESS.test(address) || known.has(address.toLowerCase())) {
            return;
        }
        known.add(address.toLowerCase());
        added.push({ name: '', email: address, subject: '', emailBody: '' });
    });

    return added;
}

/** A recipient with something to send. */
export function isReadyToSend(target: AdminMiniTarget): boolean {
    return target.subject.trim() !== '' && target.emailBody.trim() !== '';
}

export interface AdminMiniRunShape {
    name: string;
    sender: AdminMiniSender;
    schedule: AdminMiniSchedule;
    targets: AdminMiniTarget[];
    noks: ChosenNok[];
}

/**
 * What stops this run going out, in the admin's words, or an empty string when nothing does.
 *
 * Returns the **first** problem rather than a list, on purpose: a form that reports four faults at
 * once is a form the reader has to triage, and fixing the first usually reveals whether the rest
 * were real. The order below is the order an admin fills the form in.
 *
 * A recipient with no message is **not** a blocker. HQ-API refuses those targets individually and
 * names them in its report, so a run with five complete rows and one blank is a perfectly ordinary
 * thing to send; the list marks the blank one and the count says how many will go.
 */
export function runBlocker(run: AdminMiniRunShape): string {
    if (run.name.trim() === '') {
        return 'Give this run a name first.';
    }

    if (!run.targets.some(isReadyToSend)) {
        return 'No recipient has a subject and a message yet.';
    }

    // **A run with no Noks is never scored.** Both Nok processors read the run's own list, so one
    // saved without any sends its mail, collects the reply and produces no grades - silently,
    // because an empty list is a valid shape rather than an error. That is what every admin run
    // did before 2026-09-11, and it is why this is a blocker rather than a warning.
    if ((run.noks ?? []).length === 0) {
        return 'Choose at least one Nok, or nothing will score the replies.';
    }

    const unanswered: ChosenNok[] = noksMissingAnswers(run.noks ?? []);
    if (unanswered.length > 0) {
        return `${ unanswered[0].name } still needs a value.`;
    }

    if (run.sender.senderMode === 'compliance'
        && (run.sender.fromName.trim() === '' || run.sender.fromEmail.trim() === '')) {
        return 'A run sent from a person here needs a sender name and address.';
    }

    if (run.sender.senderMode === 'compliance'
        && !LOOKS_LIKE_AN_ADDRESS.test(run.sender.fromEmail.trim())) {
        return 'That sender address does not look right.';
    }

    // Only checked when the schedule asks for it. A rolling run with no start date is valid and
    // means "from now", which is what `RunSchedule` does with it.
    if (run.schedule.scheduleType === 'date' && run.schedule.scheduledAt === '') {
        return 'Choose the date it should go out.';
    }

    return '';
}

/**
 * A short reference for a run, generated before it has an id of its own.
 *
 * Shown in the composer's title bar so an admin can quote it while the run is still being
 * assembled, and stored as the run's `msCode` or `csCode`. Moved here on 2026-09-11 from the
 * Mystery Shopper form's `utils/defaults`, which was deleted with the rest of that tree.
 *
 * Not a security token and not required to be unique: `AdminSaveAndActivate` does not check it,
 * and the run's real identity is its database id.
 */
export function createRandomString(length: number): string {
    const chars: string = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let result: string = '';
    for (let i: number = 0; i < length; i++) {
        result += chars.charAt(Math.floor(Math.random() * chars.length));
    }

    return result;
}
